import PropTypes from 'prop-types';
import { HiInformationCircle } from 'react-icons/hi2';

// Person-specific variable names per brace style (these are empty for company recipients
// with no contact person, so they benefit from a fallback).
const PERSON_NAMES = {
	single: ['firstName', 'lastName', 'fullName', 'greeting'],
	double: ['vorname', 'nachname', 'anrede', 'ansprechpartner'],
};

/**
 * Whether `text` contains a person variable WITHOUT an inline fallback — e.g. "{firstName}"
 * but not "{firstName|fallback=…}". Matches the brace style.
 * @param {string} text
 * @param {'single'|'double'} braces
 * @returns {boolean}
 */
export function hasPersonTokenWithoutFallback(text, braces = 'single') {
	if (!text) return false;
	const names = PERSON_NAMES[braces] ?? PERSON_NAMES.single;
	const open = braces === 'double' ? '\\{\\{' : '\\{';
	const close = braces === 'double' ? '\\}\\}' : '\\}';
	return names.some((name) => {
		// {name} optionally with params, but NOT containing "fallback=". Pattern is built from a
		// fixed allow-list (PERSON_NAMES), never from user input.
		const re = new RegExp(`${open}\\s*${name}\\b(?![^{}]*fallback=)[^{}]*${close}`, 'i');
		return re.test(text);
	});
}

/**
 * Subtle hint shown when a text contains person placeholders without a fallback
 * value; renders nothing otherwise.
 */
const MissingFallbackHint = ({ text, braces = 'single' }) => {
	if (!hasPersonTokenWithoutFallback(text, braces)) return null;

	return (
		<div className="flex items-start gap-2 mt-2 p-2.5 rounded-lg bg-primary-100 border border-primary-200">
			<HiInformationCircle className="w-4 h-4 text-primary-500 flex-shrink-0 mt-0.5" />
			<p className="text-[11px] text-primary-700 leading-snug">
				Dieser Text nutzt persönliche Platzhalter (z.B. Vorname). Bei Firmen-Empfängern ohne Ansprechpartner
				bleiben diese leer — klicke auf den Platzhalter und setze einen Ersatzwert.
			</p>
		</div>
	);
};

MissingFallbackHint.propTypes = {
	text: PropTypes.string,
	braces: PropTypes.oneOf(['single', 'double']),
};

export default MissingFallbackHint;
